<template>
  <div>
    <FlexCol gap-1 class="relative" ref="selectContainer">
      <Label :label="label" :required="required" :error="error" v-if="label" />
      <InputArc :disabled="disabled" :error="error">
        <template #pre-icon> </template>
        <template #input>
          <input
            v-if="!inputValue"
            v-model="search"
            :disabled="disabled"
            :placeholder="placeholder"
            class="w-full bg-transparent focus:outline-none focus:ring-0 placeholder:italic"
            @keypress.enter="searchValue()"
            @input="searchAutoComplete" />
          <span v-else class="w-full overflow-hidden text-nowrap">
            <slot :item="selected" name="selected">{{ inputText }}</slot>
          </span>
        </template>
        <template #pos-icon>
          <IconInput icon="fa-spinner fa-spin" class="border-l" v-if="loading" />
          <IconInput icon="fa-xmark" class="border-l" @click="clear()" v-if="inputValue != null && !disabled" />
          <IconInput icon="fa-magnifying-glass" class="border-l" @click="toggleModal" v-if="!disableDialog" :disabled="loading ? true : false" />
          <IconInput icon="fa-magnifying-glass" class="border-l" @click="setSearchAction" v-if="disableDialog" :disabled="loading ? true : false" />
        </template>
      </InputArc>

      <div class="absolute w-full z-10 mt-1 overflow-y-auto rounded-lg border border-cockpit-line bg-cockpit-surface p-2 text-cockpit-text shadow-cockpit max-h-[240px]" v-if="autoCompleteBox">
        <div v-for="(item, i) in autoCompleteList" :key="i" @click="setZoom(item)" class="cursor-pointer rounded px-2 py-2 hover:bg-cockpit-green/10 hover:text-cockpit-green">
          <slot name="search-item" :item="item">
            {{ resolveText(item) }}
          </slot>
        </div>
      </div>

      <!--  Controle de Erros -->
      <Error :message="error" v-if="error" />
    </FlexCol>
    <!--  Modal de Zoom -->
    <Modal v-model="modalZoom" size="w-10/12 min-h-[90vh] h-full">
      <FlexCol gap-3 class="h-full min-h-0">
        <Title :title="`Busca de ${title}`">
          <div class="w-[420px]" v-if="simpleSearch">
            <InputArc>
              <template #pre-icon> </template>
              <template #input>
                <input v-model="search" :placeholder="placeholder" class="w-full bg-transparent focus:outline-none focus:ring-0" @keypress.enter="searchAutoComplete()" />
              </template>
              <template #pos-icon>
                <IconInput icon="fa-spinner fa-spin" class="border-l" v-if="loading" />
                <IconInput icon="fa-xmark" class="border-l" @click="clearAndSearch()" v-if="search != null" />
                <IconInput icon="fa-magnifying-glass" class="border-l" />
              </template>
            </InputArc>
          </div>
          <div class="w-[180px]" v-if="!simpleSearch && filters.length > 0">
            <Filter v-model="filterObject" :filters="filters" />
          </div>
        </Title>

        <TableFull :headers="headers" :items="items" v-model="tableControl" dense class="!text-wrap" v-model:loading="loading" stickyActions>
          <template v-slot:actions="{ item }">
            <ButtonZoom primary :selected="checkSelectedZoom(item)" @click="setZoom(item)" :disabled="checkSelectedZoom(item)">
              {{ !checkSelectedZoom(item) ? 'Selecionar' : 'Selecionado' }}
            </ButtonZoom>
          </template>

          <!-- Passa todos os slots recebidos para o TableFull -->
          <template v-for="(_, name) in $slots" :key="name" v-slot:[name]="slotProps">
            <slot :name="name" v-bind="slotProps" />
          </template>
        </TableFull>
      </FlexCol>
    </Modal>
  </div>
</template>

<script setup>
import base from '@/plugins/zoom'
import { get } from '@/crud.js'
import { defineEmits, onMounted, ref, watch } from 'vue'

// Definição de Props do Componente
const props = defineProps({
  clearable: Boolean,
  database: String,
  disabled: Boolean,
  error: [Array, String],
  filter: Array,
  itemText: String,
  itemValue: String,
  itemAutocomplete: [String, Array],
  tableFilter: String,
  label: String,
  modelValue: [String, Number],
  placeholder: String,
  required: Boolean,
  completeObject: Object,
  disableDialog: Boolean,
})
// Data
const autoCompleteBox = ref(false)
const selectContainer = ref(null)

const autoCompleteList = ref([])
const emits = defineEmits(['update:modelValue', 'update:completeObject', 'searchAction'])
const filters = ref([])
const filterObject = ref([])
const inputValue = ref(props.modelValue)
const inputText = ref('')

const list = ref([])
const loading = ref(false)
const items = ref([])
const headers = ref([])
const modalZoom = ref(false)

const database = props.database
const simpleSearch = ref(!!base?.[database]?.simpleSearch)

const route = ref(null)
const search = ref(null)
const selected = ref(null)
const title = ref('')

//  Controle de Tabela
const tableControl = ref({
  page: 1,
  total: 0,
  limit: 50,
  pages: 0,
  order: [],
})

// Métodos
const checkSelectedZoom = (item) => {
  return item[props.itemValue] == inputValue.value
}

const clear = () => {
  autoCompleteBox.value = false
  filterObject.value = []
  inputText.value = null
  inputValue.value = null
  list.value = []
  search.value = null
  selected.value = null

  tableControl.value = {
    page: 1,
    total: 0,
    limit: 50,
    pages: 0,
    order: [],
  }

  emits('update:completeObject', null)
}

const deployZoom = async () => {
  await startZoom()
  startValue()
}

const loadItems = async (page = 1) => {
  loading.value = true

  const query = {
    params: {
      page: tableControl.value.page,
      limit: tableControl.value.limit,
      order: tableControl.value.order,
      filters: [],
    },
  }

  let raw = []

  const isEmptyValue = (value) => {
    if (value === null || value === undefined || value === '') return true
    if (Array.isArray(value) && value.length === 0) return true
    return false
  }

  if (props.filter && Array.isArray(props.filter)) {
    const validFilters = props.filter.filter((f) => !isEmptyValue(f.value))
    query.params.filters.push(...validFilters)
  }

  if (filterObject.value && Array.isArray(filterObject.value)) {
    const validFilters = filterObject.value.filter((f) => !isEmptyValue(f.value))
    query.params.filters.push(...validFilters)
  }

  raw = await get(`${route.value}`, query)

  loading.value = false
  items.value = raw.data

  tableControl.value.pages = Math.ceil(raw.total / tableControl.value.limit)
  tableControl.value.total = raw.total
}

const resolveText = (item) => {
  if (!item) {
    return props.modelValue
  }
  if (props.itemAutocomplete) {
    const autocompleteField = Array.isArray(props.itemAutocomplete) ? props.itemAutocomplete[0] : props.itemAutocomplete
    return `${item[autocompleteField]} - ${item[props.itemText]}`
  }
  if (!props.itemText && !props.itemValue) {
    return item
  } else if (props.itemText && !props.itemValue) {
    return item[props.itemText]
  } else if (!props.itemText && props.itemValue) {
    return item[props.itemValue]
  } else {
    return `${item[props.itemValue]} ${item[props.itemText] ? ` - ${item[props.itemText]}` : ''} `
  }
}

const resolveValue = (item) => {
  if (!item) {
    return props.modelValue
  }
  if (!props.itemValue) {
    return item
  } else {
    return item[props.itemValue]
  }
}

const clearAndSearch = async () => {
  search.value = null
  await searchAutoComplete()
}

const searchAutoComplete = async () => {
  if (!search.value || search.value.length < 2) {
    autoCompleteBox.value = false
    filterObject.value = []
    return
  }
  if (Array.isArray(props.itemAutocomplete)) {
    filterObject.value = [
      {
        column: props.itemAutocomplete,
        value: search.value,
        operator: 'or like',
      },
    ]
  } else {
    filterObject.value = [
      {
        column: props.itemAutocomplete ?? props.itemText ?? props.itemValue,
        value: search.value,
        operator: 'like',
      },
    ]
  }

  await loadItems()

  autoCompleteList.value = items.value
  if (!items.value || items.value.length === 0) {
    autoCompleteBox.value = false
    filterObject.value = []
  } else {
    autoCompleteBox.value = true
  }

  // autoCompleteBox.value = false
}

const searchValue = async () => {
  const autocompleteField = Array.isArray(props.itemAutocomplete) ? props.itemAutocomplete[0] : props.itemAutocomplete
  filterObject.value = [
    {
      column: autocompleteField ?? props.itemValue,
      value: search.value,
      tableFilter: props.tableFilter,
    },
  ]

  await loadItems()

  const field = autocompleteField ?? props.itemValue
  const rawItens = items.value

  if (!Array.isArray(rawItens)) {
    return
  }

  const item = rawItens.find((el) => el[field] == search.value)

  if (item) {
    setZoom(item)
  } else {
    clear()
  }
}

const setZoom = (item) => {
  inputText.value = resolveText(item)
  inputValue.value = resolveValue(item)
  autoCompleteBox.value = false
  modalZoom.value = false
  search.value = null
  selected.value = item
  emits('update:completeObject', item)
}

const startValue = async () => {
  const field = props.itemValue
  const value = props.modelValue

  if (!field || !value) return

  const filterObject = {
    column: field,
    value,
    operator: '=',
  }

  const query = {
    params: {
      filters: [filterObject],
    },
  }

  try {
    const selectedItem = await get(route.value || props.database, query)
    const selectedItemData = Array.isArray(selectedItem?.data) ? selectedItem.data[0] : null

    if (selectedItemData) {
      inputText.value = resolveText(selectedItemData)
      inputValue.value = resolveValue(selectedItemData)
      selected.value = selectedItemData
      emits('update:completeObject', selectedItemData)
    }
  } catch (error) {
    console.error('Erro ao buscar valor inicial:', error)
  }
}

const startZoom = async () => {
  const database = props.database

  if (database && base && base[database]) {
    route.value = base[database].route
    headers.value = [...base[database].headers, { title: 'Ações', field: 'actions', itemClass: 'w-[280px]', headerClass: 'text-center justify-center ', disableOrder: true }]
    filters.value = base[database].filters
    title.value = base[database].title ?? ''

    // Usa a ordem configurada no zoom.js, se existir
    if (base[database].order && Array.isArray(base[database].order)) {
      tableControl.value.order = base[database].order.map((order) => ({
        ...order,
        name: order.name || headers.value.find((h) => h.field === order.column)?.title || order.column,
      }))
    }

    filterObject.value = []
    if (base[database].initialFilters && Array.isArray(base[database].initialFilters)) {
      filterObject.value = [...base[database].initialFilters]
    }

    // if (inputValue.value) {
    //   filterObject.value = [
    //     {
    //       column: props.itemValue,
    //       value: inputValue.value,
    //     },
    //   ]
    // } else {
    // }
    await loadItems()
  }
}

const toggleModal = () => {
  if (loading.value) {
    return
  }
  if (props.searchAction) {
    emits('searchAction')
    return
  }
  if (!props.disabled || props.disabled == 'undefined') {
    modalZoom.value = !modalZoom.value
  }
}

const handleClickOutside = (event) => {
  if (selectContainer.value?.$el && !selectContainer.value.$el.contains(event.target)) {
    autoCompleteBox.value = false
  }
}

watch(autoCompleteBox, (isOpen) => {
  if (isOpen) {
    setTimeout(() => document.addEventListener('click', handleClickOutside), 0)
  } else {
    document.removeEventListener('click', handleClickOutside)
  }
})

const setSearchAction = () => {
  emits('searchAction')
}
// Hooks
onMounted(async () => {
  inputValue.value = props.modelValue
  deployZoom()
})
// Watchers
watch(
  () => tableControl.value,
  () => {
    loadItems()
  },
  { deep: true },
)

watch(
  filterObject,
  (val, oldVal) => {
    if (JSON.stringify(val) !== JSON.stringify(oldVal)) {
      const isEmptyValue = (value) => {
        if (value === null || value === undefined || value === '') return true
        if (Array.isArray(value) && value.length === 0) return true
        return false
      }
      if (Array.isArray(filterObject.value)) {
        const cleanedFilters = filterObject.value.filter((f) => !isEmptyValue(f.value))
        if (cleanedFilters.length !== filterObject.value.length) {
          filterObject.value = cleanedFilters
          return
        }
      }

      tableControl.value.page = 1
      loadItems()
    }
  },
  { deep: true },
)

watch(inputValue, (value) => {
  if (value == '') {
    value = null
  }
  emits('update:modelValue', value)
})

watch(
  () => props.modelValue,
  (value) => {
    if (value == '') {
      value = null
    }
    if (value != inputValue.value) {
      inputValue.value = value
      deployZoom()
    }
  },
  { immediate: true },
)

watch(
  () => props.filter,
  (newVal, oldVal) => {
    const changed = JSON.stringify(newVal) !== JSON.stringify(oldVal)
    if (changed) {
      tableControl.value.page = 1
      loadItems()
    }
  },
  { deep: true },
)
</script>
